import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { useCurrencyRates } from '@/api/currency/queries/useCurrencyRates'
import { PaymentMethod } from '@/api/payments/types/CreatePaymentLinkPayload'
import { verifyPayment } from '@/api/payments/paymentsService'
import { Divider } from '@/components/Divider'
import ClockIcon from '@/components/icons/clock.svg?react'
import CostIcon from '@/components/icons/cost.svg?react'
import PackageIcon from '@/components/icons/package.svg?react'
import SuccessIcon from '@/components/icons/success.svg?react'
import { TranzilaHostedFields } from '@/components/TranzilaHostedFields'
import type { TranzilaCheckoutMode } from '@/components/TranzilaHostedFields'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { getShippingMethodLabel } from '@/helpers/pkgUtils'
import { useToast } from '@/hooks/useToast'
import { trackPaymentSuccessful } from '@/lib/analytics'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { RoutePath } from '@/RoutePath'

import { HostedFieldsData } from './components/PaymentData'
import { ShippingOptions } from './ShippingPage'

const VERIFY_POLL_INTERVAL_MS = 2000
const VERIFY_MAX_ATTEMPTS = 8

interface Props {
  shippingOptions: ShippingOptions
  hostedFieldsData: HostedFieldsData | null
  pkgId: string
  totalPrice: number
  paymentMethod: PaymentMethod
}

function hostedFieldsCheckoutMode(method: PaymentMethod): TranzilaCheckoutMode {
  return method === PaymentMethod.Bit ? 'bit' : 'card'
}

export default function ProcessPage({ shippingOptions, hostedFieldsData, pkgId, totalPrice, paymentMethod }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: currencyRates } = useCurrencyRates()
  const { showErrorToast } = useToast()

  const queryClient = useQueryClient()

  const [isVerifying, setIsVerifying] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [chargePrice, setChargePrice] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const totalILSPrice = totalPrice * 0.9 * 1.03 * (currencyRates?.poalimRate || 0) + totalPrice * 0.1 * (currencyRates?.BOIrate || 0)

  const calculateDeliveryDates = useCallback(() => {
    const today = new Date()
    const addBusinessDays = (date: Date, days: number) => {
      let count = 0
      const result = new Date(date)
      while (count < days) {
        result.setDate(result.getDate() + 1)
        const dayOfWeek = result.getDay()
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++
        }
      }
      return result
    }

    const minDate = addBusinessDays(today, 14)
    const maxDate = addBusinessDays(today, 21)

    return t('payment.deliveryDateRange', { minDate, maxDate })
  }, [t])

  const startVerifyPolling = useCallback(() => {
    setIsVerifying(true)
    let attempts = 0

    const poll = async () => {
      attempts++
      try {
        const { isPaid } = await verifyPayment(pkgId)
        if (isPaid) {
          if (pollRef.current) clearInterval(pollRef.current)
          setIsVerifying(false)
          setShowSuccessDialog(true)
          return
        }
      } catch {
        // keep polling
      }
      if (attempts >= VERIFY_MAX_ATTEMPTS) {
        if (pollRef.current) clearInterval(pollRef.current)
        setIsVerifying(false)
        setShowSuccessDialog(true)
      }
    }

    poll()
    pollRef.current = setInterval(poll, VERIFY_POLL_INTERVAL_MS)
  }, [pkgId])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  useEffect(() => {
    if (!hostedFieldsData) {
      navigate(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }, [hostedFieldsData, pkgId, navigate])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return
    if (!showSuccessDialog || !pkgId) return
    const value = chargePrice ? parseFloat(chargePrice) : totalILSPrice
    trackPaymentSuccessful({
      packageId: pkgId,
      value: Number.isFinite(value) ? value : undefined,
    })
  }, [showSuccessDialog, pkgId, chargePrice, totalILSPrice])

  const handleChargeSuccess = useCallback(
    (response: TranzilaChargeResponse) => {
      setChargePrice(response.transaction_response?.amount ?? null)
      startVerifyPolling()
    },
    [startVerifyPolling]
  )

  const chargeErrorTitle = paymentMethod === PaymentMethod.Bit ? t('payment.bitChargeError') : t('payment.chargeError')

  const handleChargeError = useCallback(
    (error: string) => {
      showErrorToast(chargeErrorTitle, error)
    },
    [chargeErrorTitle, showErrorToast]
  )

  const onBackToUserPkgs = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PACKAGES] })
    setShowSuccessDialog(false)
    navigate(RoutePath.ClientPkg)
  }

  if (!hostedFieldsData) return null

  return (
    <div className='space-y-4'>
      {!showSuccessDialog && !isVerifying && (
        <TranzilaHostedFields
          hostedFieldsData={hostedFieldsData}
          checkoutMode={hostedFieldsCheckoutMode(paymentMethod)}
          onSuccess={handleChargeSuccess}
          onError={handleChargeError}
        />
      )}

      {isVerifying && (
        <Card>
          <CardContent className='flex flex-col items-center gap-6 py-12'>
            <Spinner className='size-8' />
            <p className='text-text-primary text-sm font-medium'>{t('payment.verifyingPayment')}</p>
          </CardContent>
        </Card>
      )}

      {showSuccessDialog && (
        <Card>
          <CardContent className='flex flex-col items-center gap-4'>
            <div className='h-6 w-6'>
              <SuccessIcon className='text-icon-primary h-full w-full' />
            </div>

            <div className='flex flex-col items-center gap-4'>
              <h2 className='text-text-primary text-center text-base font-medium'>{t('payment.packageOnTheWay')}</h2>
              <p className='text-text-secondary text-center text-sm'>
                {t('payment.successMessage')}
                <br />
                {t('payment.successMessage2')}
                <br />
                {t('payment.successMessage3')}
              </p>
            </div>

            <div className='bg-button-primary-selected-segment w-full rounded-lg p-4'>
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <CostIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    {t('payment.shippingCostTotal')} {chargePrice ?? totalILSPrice.toFixed(2)} ₪
                  </span>
                </div>

                <div className='flex items-center gap-2'>
                  <PackageIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    {t('payment.shippingType')}: {getShippingMethodLabel(shippingOptions.selectedShippingMethod, t)}
                  </span>
                </div>

                <div className='flex items-center gap-2'>
                  <ClockIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    {t('payment.deliveryExpected')} {calculateDeliveryDates()}
                  </span>
                </div>
              </div>
            </div>
            <p className='text-text-secondary self-start text-xs'>{t('payment.cancelBeforeShip')}</p>
            <Divider />
            <Link to={RoutePath.ClientPkg}>
              <Button size='sm' variant='ghost' onClick={onBackToUserPkgs} className='text-text-link text-sm'>
                {t('payment.backToPackages')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
